import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import ApplyTaskTopicAssociations from 'actions/conversation_item/task/apply_task_topic_associations';
import Button, { ButtonTypes } from 'components/common/button';
import Chip from 'components/common/chip';
import connect from 'components/lib/connect';
import TaskTopicsActionConnector from 'components/customer/conversation_history/conversation_items_v2/task/task_topics_action_connector';
import UpdateTaskCustomAttributes from 'actions/conversation_item/task/update_task_custom_attributes';
import { useExecuteAction } from 'components/hooks/connect_hooks';

// Pill display for a task's applied topics and custom attributes, replacing the
// shared multi-select's truncated comma summary. Each applied value renders as a
// removable Chip (the shared design-system pill); a "+ Add topic" link opens the
// existing multi-select menu (reused wholesale) to add more. The shared control's
// collapsed comma display and chevron are hidden via StyledEditor so only its
// dropdown menu (and the search input in its opener) surface.
export function TaskTopicsPills({
  className,
  customAttributesImmutable,
  customAttributesProvider,
  getTopic,
  task,
  taskId,
  topicsImmutable,
  topicsProvider,
}) {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();
  const [openRequestedAt, setOpenRequestedAt] = useState();

  const appliedTopics = useMemo(
    () =>
      _.compact(
        (task.topicIds || []).map(id => {
          const topic = getTopic(id);
          return topic && { id, name: topic.getNameWithAncestry() };
        })
      ),
    // topicsImmutable forces a recompute when a topic is renamed or archived
    // (which doesn't change task.topicIds). It's not read in the callback, so
    // exhaustive-deps flags it as "unnecessary".
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [task.topicIds, topicsImmutable, getTopic]
  );

  const appliedAttributes = useMemo(
    () =>
      (task.customAttributes || []).map(attr => {
        const definition = customAttributesProvider.findBy({ id: attr.id });
        return {
          id: attr.id,
          value: attr.value,
          label: definition ? definition.label : attr.id,
          deactivated: !!(definition && definition.deactivated),
        };
      }),
    // customAttributesImmutable forces a recompute when a definition's label or
    // deactivated flag changes (which doesn't change task.customAttributes). It's
    // not read in the callback, so exhaustive-deps flags it as "unnecessary".
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [task.customAttributes, customAttributesImmutable, customAttributesProvider]
  );

  const removeTopic = useCallback(
    id => executeAction(ApplyTaskTopicAssociations, { taskId, topicChanges: { add: [], remove: [id] } }),
    [executeAction, taskId]
  );

  const removeAttribute = useCallback(
    attr =>
      executeAction(UpdateTaskCustomAttributes, {
        taskId,
        customAttributeUpdates: { add: [], remove: [{ id: attr.id, value: attr.value }] },
      }),
    [executeAction, taskId]
  );

  const openMenu = useCallback(() => setOpenRequestedAt(new Date().toISOString()), []);

  // Topics/attributes are read-only on a closed task: no pill removal, no
  // add affordance, and no editor. Agents re-open the task to edit them.
  const isClosed = !!(task.isClosed && task.isClosed());

  return (
    <div className={className}>
      <PillRow>
        {appliedTopics.map(topic => (
          <Chip
            key={`topic-${topic.id}`}
            label={topic.name}
            onDelete={isClosed ? undefined : () => removeTopic(topic.id)}
            small
            title={topic.name}
          />
        ))}
        {appliedAttributes.map(attr => {
          const label = `${attr.label}: ${attr.value}`;
          return (
            <Chip
              disabled={attr.deactivated}
              key={`attr-${attr.id}-${attr.value}`}
              label={label}
              onDelete={isClosed || attr.deactivated ? undefined : () => removeAttribute(attr)}
              small
              title={label}
            />
          );
        })}
        {!isClosed && (
          <AddTopicLink buttonType={ButtonTypes.TEXT} onClick={openMenu}>
            {t('Add topic')}
          </AddTopicLink>
        )}
      </PillRow>
      {!isClosed && (
        <StyledEditor>
          <TaskTopicsActionConnector
            customAttributesImmutable={customAttributesImmutable}
            customAttributesProvider={customAttributesProvider}
            getTopic={getTopic}
            openRequestedAt={openRequestedAt}
            task={task}
            taskId={taskId}
            topicsImmutable={topicsImmutable}
            topicsProvider={topicsProvider}
          />
        </StyledEditor>
      )}
    </div>
  );
}

TaskTopicsPills.propTypes = {
  className: PropTypes.string,
  // Observed store binding; a dependency so chips refresh on definition changes.
  customAttributesImmutable: PropTypes.object,
  customAttributesProvider: PropTypes.shape({ findBy: PropTypes.func.isRequired }).isRequired,
  getTopic: PropTypes.func.isRequired,
  task: PropTypes.object.isRequired,
  taskId: PropTypes.string.isRequired,
  // Observed store binding; a dependency so chips refresh on topic changes.
  topicsImmutable: PropTypes.object,
  topicsProvider: PropTypes.shape({ findAll: PropTypes.func.isRequired }).isRequired,
};

const PillRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing.medium};
`;

// "+ Add topic" affordance that opens the topics menu. Styled as a green inline
// link (shared TEXT button) rather than a pill so it reads as an action, not a
// value. The "+" is a decorative pseudo-element (kept out of the translated key).
const AddTopicLink = styled(Button)`
  align-items: center;
  color: ${p => p.theme.colors.green400};
  display: inline-flex;
  font-size: ${p => p.theme.fontSize.reduced};
  font-weight: ${p => p.theme.fontWeight.medium};
  /* Match the small chip's height so the label centers with the chip text. */
  min-height: 24px;
  padding: 0;

  &::before {
    content: '+';
    margin-right: 4px;
  }

  &:hover {
    color: ${p => p.theme.colors.green500};
  }
`;

const StyledEditor = styled.div`
  /* Reuse the shared topics menu purely as the "+ Add topic" affordance: hide
     its collapsed comma-list display and chevron so only the dropdown menu
     (and its search input, which lives in the opener) surface when opened. */
  .conversationTopic-display {
    display: none;
  }

  .conversationTopic-multi::after {
    display: none;
  }

  .conversationTopic-multi {
    height: auto;
    justify-content: flex-start;
    max-width: 100%;
    min-height: 0;
  }

  .conversationTopic-menuOpener {
    padding-left: 0;
  }
`;

function mapStateToProps({ getProvider }, { taskId }) {
  const conversationHistoryProvider = getProvider('conversationHistory');
  const customAttributesProvider = getProvider('customAttributes');
  const topicsProvider = getProvider('topics');

  const taskItem = conversationHistoryProvider.findBy({ id: taskId });

  return {
    customAttributesProvider,
    customAttributesImmutable: customAttributesProvider.immutableStore.binding.get(),
    getTopic: id => topicsProvider.findBy({ id }),
    task: taskItem && taskItem.content,
    taskId,
    topicsProvider,
    topicsImmutable: topicsProvider.immutableStore.binding.get(),
  };
}

function TaskTopicsPillsGuard(props) {
  if (!props.task) {
    return null;
  }
  return <TaskTopicsPills {...props} />;
}

TaskTopicsPillsGuard.propTypes = {
  task: PropTypes.object,
};

const TaskTopicsPillsContainer = connect(mapStateToProps)(TaskTopicsPillsGuard);

TaskTopicsPillsContainer.propTypes = {
  taskId: PropTypes.string.isRequired,
};

export default TaskTopicsPillsContainer;
